/**
 * How each skill looks when it is used: the body motion it plays, the way it gathers while charging,
 * the element its effects are tinted in, and which category of effect it gets (a weapon slash, a
 * thrust, sword qi, a palm force wave, an area slam, a heal, a buff).
 *
 * Chosen here in the client from what the skill data already says (its kind, `fx`, area, dash,
 * status, art and element) and the weapon the caster holds, so new skills get a fitting look with
 * no extra data; `SKILL_FX` overrides any part of it for one skill by id. Purely visual: nothing in
 * the simulation reads it.
 */
import { artOfSkill, type SkillDef } from '@nine/shared';
import type { WeaponKind } from './rig/weapons';
import { LOOKS, MOTIONS, type MotionId } from './sprites';
import { WX_MIN_SWING, WX_MOTION, WX_SWINGS, wxMotionOf, type WxStyle } from './ink/game';

/** How a swing is drawn: which motion the renderer plays and which trail the effects draw. */
export type SwingStyle =
  | 'cleave'
  | 'rising'
  | 'thrust'
  /** Double palm push (the 'palmPush' motion). */
  | 'palm'
  | 'spin'
  | 'overhead'
  | 'chest'
  /** Two-handed spear thrust. */
  | 'spear'
  /** Spinning fan sweep. */
  | 'fan'
  /** Leaping downward strike. */
  | 'leap'
  /** Sword-qi release. */
  | 'qi'
  /** Meditative healing cast. */
  | 'heal'
  /** Ultimate / rage move. */
  | 'ultimate'
  // เหมันต์สะกดธาร's warps (render/fx/snowblink.ts): the first drawn from the low stance, then a rising
  // cut, a falling cleave, a thrust and a level sweep in turn, the last a cleave flicked clean (or,
  // when the chain ends before its last cut, the blade flicked clean from where it stopped).
  | 'snowOpen'
  | 'snowRise'
  | 'snowCleave'
  | 'snowThrust'
  | 'snowSweep'
  | 'snowFinale'
  | 'snowFinish'
  // The re-keyed skill motions (render/motions/wx.ts; C0-VFX, ported by C0-VFX-PREP).
  | WxStyle;

/** How a fighter gathers while charging a skill (the pose of its wind-up). */
export type CastStyle = 'overhead' | 'chest' | 'lowGuard' | 'cast' | 'palm' | 'spear' | 'meditate' | 'rage';

/** The weapon families effects tell apart (from the rig's weapon catalogue, see `FX_WEAPON`). */
export type FxWeapon = 'jian' | 'dao' | 'spear' | 'polearm' | 'fan' | 'staff' | 'fist' | 'claw' | 'sleeve';

/** Element tints of effects: natural materials and dyes (see `RAMPS` in fxSheets.ts). */
export type FxElement =
  | 'metal'
  | 'fire'
  | 'poison'
  | 'earth'
  | 'water'
  | 'wind'
  | 'thunder'
  | 'yin'
  | 'yang'
  | 'blood'
  | 'ink'
  | 'heal';

/** Broad family of a skill's effect. */
export type FxCategory =
  'slash' | 'thrust' | 'qi' | 'palm' | 'area' | 'heal' | 'buff' | 'volley' | 'warp' | 'bite';

export interface SkillLook {
  /** Body motion; a list alternates through it move by move (a combo of two cuts). */
  motion: SwingStyle | readonly SwingStyle[];
  cast: CastStyle;
  element: FxElement;
  category: FxCategory;
  /** A ranged release flying to the target: sword qi, or a palm's force wave. */
  projectile?: 'qi' | 'palm';
  /** The ground slammed: a ring of force, cracks and debris where it lands. */
  slam?: boolean;
  /** The trail drawn in place of the caster's weapon's (a dancer's long sleeves). */
  trail?: FxWeapon;
  /** A move's own mark beside its category's (see `FxExtra`). */
  extra?: FxExtra;
}

/**
 * Marks some moves leave beyond their category's effect: a shackle of chain closing on the target,
 * a qin's sound waves, a smoke bomb, caltrops strewn on the ground, a Buddhist dharma wheel turning
 * on the ground.
 */
export type FxExtra = 'shackle' | 'sound' | 'smoke' | 'caltrops' | 'dharma';

/** The rig's weapon kinds grouped into the families effects draw differently. */
export const FX_WEAPON: Readonly<Record<WeaponKind, FxWeapon>> = {
  jian: 'jian',
  kris: 'jian',
  brush: 'jian',
  flute: 'jian',
  whisk: 'jian',
  dao: 'dao',
  axe: 'dao',
  hook: 'dao',
  spear: 'spear',
  qiang: 'spear',
  glaive: 'polearm',
  halberd: 'polearm',
  fan: 'fan',
  umbrella: 'fan',
  staff: 'staff',
  hammer: 'staff',
  spike: 'staff',
  chain: 'staff',
  whip: 'staff',
  fist: 'fist',
  bow: 'fist',
};

/** The classic looks' weapons (`Look.weapon`) as effect families. */
const LOOK_WEAPON = { sword: 'jian', sabre: 'dao', staff: 'staff', none: 'fist' } as const;

/**
 * The weapon family a fighter strikes with: the weapon of their look, bare hands, or (a creature
 * with no look) claws and fangs. `humanoid` is whether the figure has strike poses.
 */
export function fxWeaponOf(e: { sprite: string }, humanoid: boolean): FxWeapon {
  const look = LOOKS[e.sprite];
  if (look) return LOOK_WEAPON[look.weapon];
  return humanoid ? 'jian' : 'claw';
}

/** Overrides for every move of an art (by art id), under the per-skill ones. */
export const ART_FX: Readonly<Record<string, Partial<SkillLook>>> = {
  // ระบำมาร: the dancer's long sleeves trail like ribbons, the strikes spun out of the dance.
  'chorus': { trail: 'sleeve', element: 'yin' },
  // กระบี่จันทรา: violet moonlight, even where its cuts poison.
  'listening-blade': { element: 'yin' },
};

/** Per-skill overrides (by skill id) over what `skillLook` works out from the data. */
export const SKILL_FX: Readonly<Record<string, Partial<SkillLook>>> = {
  // เก้ากระบี่: the stone's sword arts read as bright steel sword qi.
  'ns-pierce': { element: 'metal', projectile: 'qi' },
  'ns-rush': { element: 'metal' },
  'ns-sweep': { element: 'metal' },
  // The palm roar and the lion's roar push a wall of force outward.
  'cp-roar': { motion: 'palm', projectile: 'palm' },
  'ar-lion': { motion: 'palm', slam: true },
  // Thrown needles: a flick of the wrist, not a cut.
  'jl-needle': { motion: 'qi', element: 'poison' },
  'knife-throw': { motion: 'qi', element: 'metal' },
  // Creatures whose bite carries venom.
  'viper-fang': { element: 'poison' },
  'spider-fang': { element: 'poison' },
  'spider-web': { element: 'poison' },
  'centipede-bite': { element: 'poison' },
  'scorpion-pincer': { element: 'poison' },
  'scorpion-sting': { element: 'poison' },
  'fox-charm': { element: 'yin' },
  'jiangshi-claw': { element: 'yin' },
  // The monk's staff sweep and the boss's great cleave: a leap down onto the target.
  'monk-sweep': { motion: 'leap', slam: true },
  'boss-cleave': { motion: 'leap', slam: true },
  'bear-maul': { slam: true },
  'wb-rain': { element: 'water' },
  'boss-ring': { element: 'fire' },
  // ระบำมาร: strikes spun out of the dance (its heal, buff and ultimate keep their own motions).
  'dd-sleeve': { motion: ['fan', 'rising'] },
  'dd-twirl': { motion: 'fan' },
  'dd-drain': { motion: ['fan', 'rising'] },
  'dd-petals': { motion: 'fan' },
  'dd-mist': { motion: 'fan' },
  // ฝ่ามือปรัชญา: the dharma wheel under the area palms.
  'pp-dharma': { extra: 'dharma' },
  'pp-shatter': { extra: 'dharma' },
  'pp-tathagata': { extra: 'dharma' },
  // กระบี่ปลายแท่งหมึก: the qin's notes spread as sound waves.
  'sf-qin': { extra: 'sound', element: 'water' },
  // วิชาลับ: a smoke bomb, and caltrops strewn on the ground.
  'hw-smoke': { extra: 'smoke' },
  'hw-caltrops': { extra: 'caltrops', element: 'metal' },
  // ห่วงโซ่ (มือจับกุม): chains closing on the target.
  'ah-chain': { extra: 'shackle' },
  // C0-VFX: the re-keyed motions of the moves the effect samples replace (render/ink/game.ts).
  ...Object.fromEntries(Object.entries(WX_MOTION).map(([id, motion]) => [id, { motion }])),
};

const PALM_FX = new Set<SkillDef['fx']>(['palm', 'vajra', 'taiji', 'shadow', 'bell']);
const SPIN_FX = new Set<SkillDef['fx']>(['whirl', 'blizzard', 'ink', 'bloodstorm']);
const THRUST_FX = new Set<SkillDef['fx']>(['pierce', 'seal', 'snowdash', 'grab']);
const QI_FX = new Set<SkillDef['fx']>(['wind', 'brush', 'needles']);

/** The motion of a skill used with this weapon. */
function motionOf(s: SkillDef, w: FxWeapon): SwingStyle | readonly SwingStyle[] {
  if (s.kind === 'heal') return 'heal';
  if (s.kind === 'buff') return 'chest';
  if (s.spin) return 'spin';
  if (s.blink) return 'cleave';
  if (s.kind === 'power') return 'ultimate';
  const pole = w === 'spear' || w === 'polearm';
  if (s.fx === 'quake') return 'leap';
  if (s.fx === 'bolt' || s.fx === 'nine') return 'overhead';
  if (SPIN_FX.has(s.fx)) return w === 'fan' ? 'fan' : 'spin';
  if (s.dash === 'target') return w === 'fist' ? 'palm' : w === 'jian' ? 'thrust' : 'leap';
  if (PALM_FX.has(s.fx)) return s.aoe && s.fx === 'shadow' ? 'spin' : 'palm';
  if (THRUST_FX.has(s.fx)) return pole ? 'spear' : w === 'fist' ? 'palm' : 'thrust';
  if (QI_FX.has(s.fx)) return w === 'fist' && s.fx !== 'needles' ? 'palm' : 'qi';
  if (s.fx === 'crush') return w === 'claw' ? 'cleave' : 'overhead';
  switch (w) {
    case 'fan':
      return ['fan', 'rising'];
    case 'fist':
      return ['palm', 'rising'];
    case 'spear':
    case 'polearm':
      return ['spear', 'cleave'];
    case 'staff':
      return ['overhead', 'cleave'];
    default:
      return ['cleave', 'rising'];
  }
}

/** The pose a skill gathers in while it charges. Break moves always rise high (破 reads as a raised blow). */
function castOf(s: SkillDef, motion: SwingStyle): CastStyle {
  if (s.kind === 'break' || s.fx === 'bolt' || s.fx === 'nine') return 'overhead';
  if (s.kind === 'heal') return 'meditate';
  if (s.kind === 'power') return 'rage';
  if (motion === 'palm') return 'palm';
  if (motion === 'spear') return 'spear';
  if (motion === 'thrust') return 'lowGuard';
  if (s.kind === 'buff') return 'chest';
  return 'cast';
}

/** The element a skill's effects are tinted in. */
function elementOf(s: SkillDef): FxElement {
  if (s.kind === 'heal') return 'heal';
  if (s.applies?.status === 'poison' || s.fx === 'needles') return 'poison';
  switch (s.fx) {
    case 'bolt':
      return 'thunder';
    case 'snow':
    case 'snowdash':
    case 'blizzard':
    case 'snowstep':
    case 'icebreak':
    case 'snowpride':
    case 'snowblink':
      return 'water';
    case 'quake':
      return 'earth';
    case 'vajra':
    case 'bell':
      return 'yang';
    case 'blood':
    case 'bloodstorm':
      return 'blood';
    case 'sabre':
    case 'chain': {
      // A heavy blade's sparks in its art's element: red-gold for yang, violet for yin.
      const el = artOfSkill(s.id)?.element;
      return el === 'yin' ? 'yin' : el === 'soft' ? 'wind' : el === 'hard' ? 'metal' : 'fire';
    }
    case 'shadow':
      return 'yin';
    case 'brush':
    case 'ink':
    case 'seal':
      return 'ink';
    case 'wind':
      return 'wind';
    case 'bite':
      return 'blood';
    case 'crush':
      return 'earth';
  }
  const art = artOfSkill(s.id);
  switch (art?.element) {
    case 'yin':
      return 'yin';
    case 'soft':
      return 'wind';
    case 'taiji':
      return 'water';
    case 'hard':
      return 'earth';
    default:
      return 'metal';
  }
}

function categoryOf(s: SkillDef, motion: SwingStyle, w: FxWeapon): FxCategory {
  if (s.kind === 'heal') return 'heal';
  if (s.kind === 'buff') return 'buff';
  if (s.volley) return 'volley';
  if (s.blink) return 'warp';
  if (s.aoe || s.ground || s.telegraph || s.fx === 'quake') return 'area';
  if (w === 'claw') return 'bite';
  if (motion === 'palm') return 'palm';
  if (motion === 'qi') return 'qi';
  if (motion === 'thrust' || motion === 'spear') return 'thrust';
  return 'slash';
}

const LOOKS_CACHE = new Map<string, SkillLook>();

/** How a skill looks used by a fighter with this weapon (cached per skill and weapon). */
export function skillLook(s: SkillDef, w: FxWeapon): SkillLook {
  const key = `${s.id}|${w}`;
  const done = LOOKS_CACHE.get(key);
  if (done) return done;
  const own = motionOf(s, w);
  const first = typeof own === 'string' ? own : own[0]!;
  const category = categoryOf(s, first, w);
  // C0-VFX: every move re-keyed by kind (render/ink/game.ts `wxMotionOf`); a two-cut pair stays a pair.
  const wxm = wxMotionOf(s, category, first, w);
  const motion: SkillLook['motion'] =
    wxm === 'wxCleave' && typeof own !== 'string' && own[1] === 'rising' ? ['wxCleave', 'wxRise'] : (wxm ?? own);
  const ranged = s.range > 2.2 && !s.dash && !s.blink && !s.volley && !s.ground;
  const look: SkillLook = {
    motion,
    cast: castOf(s, first),
    element: elementOf(s),
    category,
    ...(ranged && w !== 'claw' && (category === 'slash' || category === 'qi' || category === 'thrust')
      ? { projectile: w === 'fist' ? 'palm' : 'qi' }
      : ranged && category === 'palm'
        ? { projectile: 'palm' }
        : {}),
    ...(s.fx === 'quake' || first === 'leap' || (s.ground && !!s.aoe) ? { slam: true } : {}),
    // A shackling hit closes a chain on the target.
    ...(s.applies?.status === 'shackle' ? { extra: 'shackle' as const } : {}),
    ...ART_FX[artOfSkill(s.id)?.id ?? ''],
    ...SKILL_FX[s.id],
  };
  // C0-VFX: an override's base motion (a palm roar, a monk's leap) re-keyed as well.
  if (wxm && !(s.id in WX_MOTION)) {
    const m = look.motion;
    look.motion = typeof m === 'string' ? (WX_REMAP[m] ?? m) : m.map((x) => WX_REMAP[x] ?? x);
  }
  LOOKS_CACHE.set(key, look);
  return look;
}

/** The base swings and the re-keyed motions that replace them (C0-VFX). */
const WX_REMAP: Partial<Record<SwingStyle, SwingStyle>> = {
  cleave: 'wxCleave',
  rising: 'wxRise',
  thrust: 'wxThrust',
  palm: 'wxPalm',
  qi: 'wxQi',
  leap: 'wxSlam',
  heal: 'wxHeal',
  ultimate: 'wxPower',
  chest: 'wxBuff',
  overhead: 'wxBreak',
};

/**
 * Seconds a swing of each style is shown for at least (when the fighter stands still after it): the
 * simulation's swing is a quarter second, too short to read a leap, a spin or an ultimate in.
 */
export const MIN_SWING: Readonly<Partial<Record<SwingStyle, number>>> = {
  palm: 0.28,
  spear: 0.3,
  qi: 0.32,
  fan: 0.36,
  leap: 0.42,
  heal: 0.5,
  ultimate: 0.55,
  // A warp lasts as long as the chain waits between warps (the skill's `blink`).
  snowOpen: 0.4,
  snowRise: 0.4,
  snowCleave: 0.4,
  snowThrust: 0.4,
  snowSweep: 0.4,
  snowFinale: 1.1,
  snowFinish: 0.75,
  ...WX_MIN_SWING,
};

/**
 * How a skill motion plays through a swing (renderer.ts): `pts` are `[k, key]` points, `k` the part
 * of the swing (0–1) and `key` how far through the motion's keys — two points on one key hold it
 * (the wind-up, held so the move reads before it lands). The blow lands at `contact`, where a
 * landed hit freezes it on key `strike` (the hit-stop); the body drives `reach` art pixels forward
 * by then. `hop` lifts the body off the ground over part of the swing (a leap), `turn` whirls it
 * round through the views (a spin).
 */
export interface SkillSwing {
  id: MotionId;
  pts: readonly (readonly [number, number])[];
  contact: number;
  strike: number;
  reach: number;
  hop?: { from: number; to: number; h: number };
  turn?: { from: number; to: number };
  /** Played after it (renderer.ts `recovering`) when the motion does not end in the stance. */
  recover?: MotionId;
}

/** Where a leaping strike lands, as a part of its swing (the ground slam waits for it). */
export const LEAP_LANDS = 0.62;

export const SKILL_SWINGS: Readonly<Partial<Record<SwingStyle, SkillSwing>>> = {
  spear: {
    id: 'spear',
    pts: [
      [0, 0],
      [0.22, 0],
      [0.42, 1],
      [0.7, 1],
      [1, 2],
    ],
    contact: 0.42,
    strike: 1,
    reach: 5,
  },
  fan: {
    id: 'fan',
    pts: [
      [0, 0],
      [0.15, 0],
      [0.55, 1],
      [0.75, 2],
      [1, 2],
    ],
    contact: 0.75,
    strike: 2,
    reach: 2,
    turn: { from: 0.2, to: 0.7 },
    recover: 'recoverFan',
  },
  palm: {
    id: 'palmPush',
    pts: [
      [0, 0],
      [0.2, 0],
      [0.38, 1],
      [0.65, 1],
      [1, 2],
    ],
    contact: 0.38,
    strike: 1,
    reach: 4,
  },
  leap: {
    id: 'leap',
    pts: [
      [0, 0],
      [0.15, 0],
      [0.5, 1],
      [LEAP_LANDS, 2],
      [1, 2],
    ],
    contact: LEAP_LANDS,
    strike: 2,
    reach: 6,
    hop: { from: 0.12, to: LEAP_LANDS, h: 14 },
    recover: 'recoverSlam',
  },
  qi: {
    id: 'qi',
    pts: [
      [0, 0],
      [0.25, 0],
      [0.45, 1],
      [0.75, 1],
      [1, 2],
    ],
    contact: 0.45,
    strike: 1,
    reach: 3,
  },
  heal: {
    id: 'heal',
    pts: [
      [0, 0],
      [0.4, 1],
      [0.75, 2],
      [1, 2],
    ],
    contact: 1,
    strike: 2,
    reach: 0,
    recover: 'recoverGather',
  },
  ultimate: {
    id: 'ultimate',
    pts: [
      [0, 0],
      [0.3, 0],
      [0.55, 1],
      [0.72, 2],
      [1, 2],
    ],
    contact: 0.72,
    strike: 2,
    reach: 7,
    hop: { from: 0.3, to: 0.72, h: 6 },
    recover: 'recoverRage',
  },
  // เหมันต์สะกดธาร. Each warp's wind-up key is held a moment (the first, the draw-stance with frost
  // gathering on the blade, longest) before the cut.
  snowOpen: {
    id: 'snowSweep',
    pts: [
      [0, 0],
      [0.3, 0],
      [0.48, 1],
      [0.62, 1],
      [1, 2],
    ],
    contact: 0.48,
    strike: 1,
    reach: 4,
  },
  snowSweep: {
    id: 'snowSweep',
    pts: [
      [0, 0],
      [0.12, 0],
      [0.3, 1],
      [0.45, 1],
      [1, 2],
    ],
    contact: 0.3,
    strike: 1,
    reach: 4,
  },
  snowRise: {
    id: 'snowRise',
    pts: [
      [0, 0],
      [0.12, 0],
      [0.3, 1],
      [0.45, 1],
      [1, 2],
    ],
    contact: 0.3,
    strike: 1,
    reach: 3,
    hop: { from: 0.2, to: 0.6, h: 4 },
  },
  snowCleave: {
    id: 'snowCleave',
    pts: [
      [0, 0],
      [0.12, 0],
      [0.26, 1],
      [0.5, 2],
      [1, 2],
    ],
    contact: 0.26,
    strike: 1,
    reach: 4,
  },
  snowThrust: {
    id: 'snowThrust',
    pts: [
      [0, 0],
      [0.12, 0],
      [0.28, 1],
      [0.55, 1],
      [1, 2],
    ],
    contact: 0.28,
    strike: 1,
    reach: 6,
  },
  snowFinale: {
    id: 'snowFinale',
    pts: [
      [0, 0],
      [0.08, 0],
      [0.18, 1],
      [0.36, 1],
      [0.55, 2],
      [0.78, 3],
      [1, 3],
    ],
    contact: 0.18,
    strike: 1,
    reach: 6,
    hop: { from: 0.04, to: 0.18, h: 5 },
    recover: 'recoverSnow',
  },
  snowFinish: {
    id: 'snowFinish',
    pts: [
      [0, 0],
      [0.05, 0],
      [0.4, 1],
      [0.78, 2],
      [1, 2],
    ],
    contact: 0.05,
    strike: 0,
    reach: 0,
    recover: 'recoverSnow',
  },
  ...(WX_SWINGS as Partial<Record<SwingStyle, SkillSwing>>),
};

const smooth = (u: number) => {
  const v = Math.max(0, Math.min(1, u));
  return v * v * (3 - 2 * v);
};

/**
 * The frame of a skill motion at `k` (0–1) of its swing: `u` how far through the motion (0–1, for
 * `motionPhase`), the forward drive, the lift off the ground, and how far round a turn (−1: none).
 * `charged`: it came out of a cast, which held the wind-up already. `stopped`: in a hit-stop.
 */
export function skillPose(
  sw: SkillSwing,
  k: number,
  charged: boolean,
  stopped: boolean,
): { u: number; reach: number; hop: number; turn: number } {
  const pre = sw.pts[1]![0];
  const kk = charged ? pre + k * (1 - pre) : k;
  let key = sw.pts[sw.pts.length - 1]![1];
  for (let i = 1; i < sw.pts.length; i++) {
    const [k0, u0] = sw.pts[i - 1]!;
    const [k1, u1] = sw.pts[i]!;
    if (kk <= k1) {
      key = u0 + (u1 - u0) * Math.max(0, Math.min(1, (kk - k0) / Math.max(1e-6, k1 - k0)));
      break;
    }
  }
  if (stopped && kk >= sw.contact * 0.8) key = sw.strike;
  const keys = MOTIONS.find((m) => m.id === sw.id)!.keys.length;
  const reach =
    kk < pre
      ? -1
      : kk < sw.contact
        ? -1 + (sw.reach + 1) * smooth((kk - pre) / (sw.contact - pre))
        : sw.reach * (1 - 0.6 * smooth((kk - sw.contact) / (1 - sw.contact || 1)));
  const hop =
    sw.hop && kk > sw.hop.from && kk < sw.hop.to
      ? Math.sin(((kk - sw.hop.from) / (sw.hop.to - sw.hop.from)) * Math.PI) * sw.hop.h
      : 0;
  const turn =
    sw.turn && kk > sw.turn.from && kk < sw.turn.to ? (kk - sw.turn.from) / (sw.turn.to - sw.turn.from) : -1;
  return { u: key / (keys - 1), reach, hop, turn };
}

/**
 * Motions only skills play (and their gatherings): drawn ahead of need for the player alone, so a
 * crowd in a fight is not drawing every skill motion for every figure (renderer.ts `warm`).
 */
export const SKILL_MOTION_IDS: ReadonlySet<MotionId> = new Set<MotionId>([
  ...Object.values(SKILL_SWINGS).flatMap((s) => (s.recover ? [s.id, s.recover] : [s.id])),
  'castPalm',
  'castSpear',
  'castMeditate',
  'castRage',
]);

/**
 * Where a skill motion is (`k`, 0–1) at `since` seconds into a swing of `dur`, and whether it is
 * held in a hit-stop; `hitAt` is when its blow landed, from the swing's start. A blow landing with
 * the swing (as a plain attack lands, on the tick it starts) freezes the motion on its contact for
 * a moment once it gets there, then the rest plays in the time left, so it still ends on time; one
 * landing later freezes the motion where it is when it lands.
 */
export function skillSwingAt(
  sw: SkillSwing,
  since: number,
  dur: number,
  hitAt: number,
): { k: number; stopped: boolean } {
  const tc = sw.contact * dur;
  const hold = 0.06;
  const early = hitAt >= -0.03 && hitAt <= tc;
  if (early && since >= tc) {
    if (since < tc + hold) return { k: sw.contact, stopped: true };
    const rest = (since - tc - hold) / Math.max(1e-3, dur - tc - hold);
    return { k: Math.min(1, sw.contact + (1 - sw.contact) * rest), stopped: false };
  }
  const late = !early && since >= hitAt && since - hitAt < 0.07;
  return { k: Math.min(1, Math.max(0, since / dur)), stopped: late };
}
